import { HttpErrorResponse } from '@angular/common/http';

import { API_MESSAGES, ApiBusinessError } from '@core/api/api-response.model';
import { RETRY_AFTER_HEADER } from '@core/interceptors/error.interceptor';

/**
 * Turns any auth failure into one sentence to render INLINE in the form.
 *
 * Wrong credentials are never a toast: a toast is peripheral and
 * self-dismissing, and this is the single message the user is actively reading
 * and about to act on.
 *
 * Two rules run through every branch:
 *  - never confirm or deny that an identifier exists. An unknown username and
 *    a wrong password produce the same sentence, and an unknown mobile number
 *    produces the same sentence as a known one. Differentiating them turns the
 *    login screen into a user-enumeration oracle.
 *  - prefer the server's own message when it sent one, because a business
 *    rejection (HTTP 200, success: false) carries the remaining-attempts count
 *    and the lockout wording that the server alone knows.
 */
export function loginErrorMessage(error: unknown): string {
  // HTTP 200 + success: false. The server wrote this for the user; quote it.
  if (error instanceof ApiBusinessError) {
    return error.message.trim() || API_MESSAGES.failure;
  }

  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return 'Cannot reach the server. Check the network connection and try again.';
    }

    if (error.status === 401 || error.status === 400) {
      // Deliberately generic and identical for both statuses.
      return 'Those sign-in details were not accepted. Check them and try again.';
    }

    if (error.status === 403) {
      return 'This account is not permitted to sign in. Contact your administrator.';
    }

    if (error.status === 429) {
      return throttleMessage(error);
    }

    if (error.status >= 500) {
      return API_MESSAGES.failure;
    }
  }

  return API_MESSAGES.failure;
}

/** Retry-After, phrased as a wait rather than as a number of seconds to decode. */
export function throttleMessage(error: HttpErrorResponse): string {
  const seconds = retryAfterSeconds(error);
  return seconds === null
    ? 'Too many attempts. Wait a moment before trying again.'
    : `Too many attempts. Try again in ${seconds} seconds.`;
}

/** The Retry-After header as a positive whole number of seconds, or null. */
export function retryAfterSeconds(error: HttpErrorResponse): number | null {
  const header = error.headers.get(RETRY_AFTER_HEADER);
  if (!header) return null;
  const seconds = Number(header);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
}
